import * as sharedb from 'sharedb/lib/client';
import { NCBoard, UserFieldName } from '../types';
import {
	NCGraphData,
	GraphMode,
	BoardUser,
	UserSelection,
	NCNode,
	NCLink,
	NCGroup,
	NCCluster,
	NCLinkMinimal,
	PositioningLayout,
} from '../../../src/commonTypes';
import { AnalysisKey } from '../../../src/commonConstants';
import { isSameLink, minimizeLinks } from './graphs/linkUtils';
import { minimizeGroups } from './graphs/groupUtils';
import { isSameCluster } from './graphs/clusterUtils';
import {
	resolveIdeatePositioning,
	resolveIdeatePositioningLayout,
} from '../../../src/services/board/ideatePositioning';
import { nodeUserLikeKey } from './graphs/nodeLikes';

export const extractGraphData = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName,
	graphMode: GraphMode,
	isCollaborative: boolean
): NCGraphData => {
	let graphData: NCGraphData = { nodes: [], links: [] };
	if (isCollaborative && graphMode !== 'ideate') {
		const sharedGraphData: NCGraphData = doc.data[graphMode].data;
		if (sharedGraphData) {
			graphData = sharedGraphData;
		}
	} else {
		const personalGraphPath = graphMode === 'ideate' && isCollaborative ? 'ideateTeam' : graphMode;
		const personalGraphData: undefined | NCGraphData =
			doc.data[userFieldName]?.[personalGraphPath]?.data;
		if (personalGraphData) {
			graphData = personalGraphData;
		} else {
			if (graphMode === 'ideate' && isCollaborative && !graphData.analyses) {
				graphData.analyses = doc.data[userFieldName]?.ideate?.data.analyses;
				graphData.activeAnalysis = doc.data[userFieldName]?.ideate?.data.activeAnalysis;
			}
			doc.submitOp(
				[
					{
						p: [userFieldName, personalGraphPath],
						// Object data after:
						oi: {
							data: graphData,
						},
					},
				],
				{},
				shareDbCallback(`Initialised personal data for ${personalGraphPath}`)
			);
		}
	}
	if (graphMode === 'ideate') {
		const sharedPositioning = resolveIdeatePositioning(doc.data);
		if (sharedPositioning.positioningInsight) {
			graphData.positioningInsight = sharedPositioning.positioningInsight;
		}
		if (sharedPositioning.analyses.length) {
			graphData.analyses = sharedPositioning.analyses;
		}
		const sharedLayout = resolveIdeatePositioningLayout(
			doc.data,
			isCollaborative,
			graphData.positioningLayout
		);
		if (sharedLayout) {
			graphData.positioningLayout = sharedLayout;
		}
	}
	graphData.uploadedFiles = doc.data.uploadedFiles;
	graphData.template = doc.data.template;
	return graphData;
};

const handleShareDBError = (err: sharedb.Error) => {
	if (err.message.startsWith('403: Permission denied')) {
		console.error('Permission denied, user logged out so return to dashboard / login');
		window.location.href = '/';
	} else {
		console.error(err);
	}
};

export const getInspireDataForIdeate = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName,
	isCollaborative: boolean,
	colourClass: string,
	colourHex: string
) => {
	let inspireNodesToAnalyse = doc.data.inspire.data.nodes;
	let activeAnalysis: AnalysisKey | undefined;
	if (globalThis.neuroCreate.graph && globalThis.neuroCreate.graph.graphMode === 'ideate') {
		// When switching between personal/team, preserve the active analysis
		const userData = doc.data[userFieldName];
		const otherIdeateModeData = globalThis.neuroCreate.graph.isCollaborative
			? userData?.ideateTeam?.data
			: userData?.ideate?.data;
		if (otherIdeateModeData) {
			activeAnalysis = otherIdeateModeData.activeAnalysis;
		}
	}
	if (!isCollaborative) {
		// Only include nodes created by this user
		inspireNodesToAnalyse = inspireNodesToAnalyse.filter(
			(n) => n.colour === colourClass || n.colour === colourHex
		);
	}

	return {
		inspireNodesToAnalyse,
		activeAnalysis,
		input: doc.data.input || '',
		vision: doc.data.vision || '',
		summary: doc.data.summary || '',
		pdfSummary: doc.data.pdfSummary || '',
	};
};

export const makeSaveSingleUserFn = (doc: sharedb.Doc<NCBoard>) => {
	return (user: BoardUser, callback: (() => void) | null = null) => {
		if (doc.data) {
			// replace the user in the array
			const userIndex = doc.data.users.findIndex((u) => u.id === user.id);
			if (userIndex !== -1) {
				doc.submitOp(
					[
						{
							p: ['users', userIndex],
							// Object data before:
							ld: doc.data.users[userIndex],
							// Object data after:
							li: user,
						},
					],
					{},
					function (err) {
						if (err) {
							handleShareDBError(err);
						} else {
							callback && callback();
						}
					}
				);
			} else {
				console.error('User not found in users array when updating user');
			}
		}
	};
};

/** @deprecated */
export const makeSaveUsersFn = (doc: sharedb.Doc<NCBoard>) => {
	return (users: Array<BoardUser>, callback: (() => void) | null = null) => {
		if (doc.data) {
			doc.submitOp(
				[
					{
						p: ['users'],
						// Object data before:
						od: doc.data.users,
						// Object data after:
						oi: users,
					},
				],
				{},
				function (err) {
					if (err) {
						handleShareDBError(err);
					} else {
						callback && callback();
					}
				}
			);
		}
	};
};

export const makeSaveUserAnalyticsFn = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName
) => {
	return (userSelections: Array<UserSelection>, callback: (() => void) | null = null): void => {
		const path = [userFieldName, 'userSelections'];

		const dataBefore = doc.data[userFieldName]?.userSelections;

		// Submit the updated analytics data to ShareDB
		doc.submitOp(
			[
				{
					p: path,
					// Object data before:
					od: dataBefore,
					// Object data after:
					oi: userSelections,
				},
			],
			{},
			function (err) {
				if (err) {
					handleShareDBError(err);
				} else {
					console.log(`Saved user analytics data`);
					callback && callback();
				}
			}
		);
	};
};

const getGraphPathAndData = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName,
	userId: string,
	isCollaborative: boolean,
	graphMode: GraphMode
): [Array<string>, undefined | NCGraphData, string] => {
	// Choose path within the doc, based on graphMode (inspire/ideate/moodboard) and isCollaborative
	let path = isCollaborative ? [graphMode, 'data'] : [userFieldName, graphMode, 'data'];

	// Find previous data at this path as required by ShareDB
	let dataBefore: undefined | NCGraphData = isCollaborative
		? doc.data[graphMode].data
		: doc.data[userFieldName]?.[graphMode]?.data;

	console.log(
		'[doc.data[userFieldName]]',
		doc.data.users.find((u) => u.id === userId)
	);

	const colour = doc.data.users.find((u) => u.id === userId)!.colour;
	// Special case for ideate, isCollaborative
	if (graphMode === 'ideate' && isCollaborative) {
		path = [userFieldName, 'ideateTeam', 'data'];
		dataBefore = doc.data[userFieldName]?.ideateTeam?.data;
	}
	// console.log('path', path);

	return [path, dataBefore, colour];
};

const shareDbCallback = (successMessage: string, additionalShareDBCallback: (() => void) | null = null) => {
	return (err: sharedb.Error) => {
		if (err) {
			handleShareDBError(err);
		} else {
			if (additionalShareDBCallback) {
				additionalShareDBCallback();
			}
			console.log(new Date().toISOString(), successMessage);
		}
	};
};

const layoutsEqual = (
	left: PositioningLayout | undefined,
	right: PositioningLayout | undefined
): boolean => JSON.stringify(left || {}) === JSON.stringify(right || {});

const appendSharedPositioningLayoutOp = (
	ops: sharedb.Op[],
	previous: PositioningLayout | undefined,
	layout: PositioningLayout
) => {
	if (layoutsEqual(previous, layout)) return;
	ops.push({
		p: ['ideate', 'data', 'positioningLayout'],
		...(previous === undefined ? {} : { od: previous }),
		oi: layout,
	} as sharedb.ObjectReplaceOp);
};

const positioningLayoutReplaceOp = (
	path: string[],
	previous: PositioningLayout | undefined,
	layout: PositioningLayout
): sharedb.ObjectReplaceOp =>
	({
		p: [...path, 'positioningLayout'],
		...(previous === undefined ? {} : { od: previous }),
		oi: layout,
	}) as sharedb.ObjectReplaceOp;

export const makeSaveGraphFn = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName,
	userId: string,
	isCollaborative: boolean
) => {
	return (
		graphMode: GraphMode,
		graphData: NCGraphData,
		callback: (() => void) | null = null
	): void => {
		const [path, dataBefore] = getGraphPathAndData(
			doc,
			userFieldName,
			userId,
			isCollaborative,
			graphMode
		);

		if (callback) {
			globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
		}
		const positioningLayout = graphData.positioningLayout ?? dataBefore?.positioningLayout;
		const graphDataToSave: NCGraphData = positioningLayout
			? { ...graphData, positioningLayout }
			: graphData;
		const ops: sharedb.Op[] = [
			{
					p: path,
					// Object data before:
					od: dataBefore,
					// Object data after:
					oi: graphDataToSave,
			},
		];
		if (graphMode === 'ideate' && document.body.dataset.boardOwner === 'true') {
			const sharedPositioning = doc.data.ideate.data;
			const addSharedValue = <K extends 'analyses' | 'positioningInsight'>(
				key: K,
				value: NCGraphData[K]
			) => {
				if (value === undefined) return;
				const previous = sharedPositioning[key];
				if (JSON.stringify(previous) === JSON.stringify(value)) return;
				ops.push({
					p: ['ideate', 'data', key],
					...(previous === undefined ? {} : { od: previous }),
					oi: value,
				} as sharedb.ObjectReplaceOp);
			};
			addSharedValue('analyses', graphData.analyses);
			addSharedValue('positioningInsight', graphData.positioningInsight);
		}
		if (graphMode === 'ideate' && isCollaborative && positioningLayout) {
			appendSharedPositioningLayoutOp(ops, doc.data.ideate.data.positioningLayout, positioningLayout);
		}

		// Keep each user's Ideate graph personal while publishing the owner's
		// positioning choice to the board-level Ideate data.
		doc.submitOp(
			ops,
			{},
			shareDbCallback(
				`Saved graph data for ${graphMode} (${userFieldName}, ${
					isCollaborative ? 'collaborative' : 'personal'
				})`
			)
		);
	};
};

export const makeSavePositioningLayoutFn = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName,
	userId: string,
	isCollaborative: boolean,
	graphMode: GraphMode
) => {
	return (layout: PositioningLayout, callback: (() => void) | null = null): void => {
		const [path, dataBefore] = getGraphPathAndData(
			doc,
			userFieldName,
			userId,
			isCollaborative,
			graphMode
		);
		if (!dataBefore) {
			callback?.();
			return;
		}
		const previous = dataBefore.positioningLayout;
		if (layoutsEqual(previous, layout)) {
			callback?.();
			return;
		}
		const ops: sharedb.Op[] = [positioningLayoutReplaceOp(path, previous, layout)];
		if (graphMode === 'ideate' && isCollaborative) {
			appendSharedPositioningLayoutOp(ops, doc.data.ideate.data.positioningLayout, layout);
		}
		doc.submitOp(
			ops,
			{},
			shareDbCallback(
				`Saved positioning layout for ${graphMode} (${userFieldName}, ${
					isCollaborative ? 'collaborative' : 'personal'
				})`,
				callback
			)
		);
	};
};

export const makeSaveNodesFunctions = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName,
	userId: string,
	isCollaborative: boolean,
	graphMode: GraphMode
) => {
	return {
		setLike: (
			nodeUid: string,
			currentUser: Pick<BoardUser, 'id' | 'colour'>,
			liked: boolean,
			callback: (() => void) | null = null
		): void => {
			const [path, dataBefore] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);
			const nodeIndex = dataBefore?.nodes.findIndex((node) => node.uid === nodeUid) ?? -1;
			if (!dataBefore || nodeIndex < 0) {
				callback?.();
				return;
			}
			const node = dataBefore.nodes[nodeIndex];
			const userLikeKey = nodeUserLikeKey(currentUser.id);
			const likesPath = [...path, 'nodes', nodeIndex, 'likes'];
			const ops: sharedb.Op[] = [];
			if (!node.likes) {
				if (liked) ops.push({ p: likesPath, oi: { [userLikeKey]: true } });
			} else {
				if (node.likes[currentUser.colour]) {
					ops.push({ p: [...likesPath, currentUser.colour], od: true });
				}
				if (liked && !node.likes[userLikeKey]) {
					ops.push({ p: [...likesPath, userLikeKey], oi: true });
				} else if (!liked && node.likes[userLikeKey]) {
					ops.push({ p: [...likesPath, userLikeKey], od: true });
				}
			}
			if (ops.length === 0) {
				callback?.();
				return;
			}
			doc.submitOp(
				ops,
				{},
				shareDbCallback(
					`${liked ? 'Liked' : 'Unliked'} node ${nodeUid} for user ${currentUser.id}`,
					callback
				)
			);
		},
		create: (nodes: NCNode[], callback: (() => void) | null = null): void => {
			if (nodes.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore, colour] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			if (!dataBefore) {
				throw new Error("Can't save nodes without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}

			doc.submitOp(
				nodes.map((node, i) => ({
					p: [...path, 'nodes', dataBefore.nodes.length + i],
					li: node,
				})),
				{},
				shareDbCallback(
					`Created ${nodes.length} nodes (${userFieldName}, ${
						isCollaborative ? 'collaborative' : 'personal'
					})`
				)
			);
		},
		update: (nodes: NCNode[], callback: (() => void) | null = null, additionalShareDBCallback: (() => void) | null = null): void => {
			if (nodes.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore, colour] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			if (!dataBefore) {
				throw new Error("Can't update nodes without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}

			doc.submitOp(
				nodes.map((node) => ({
					p: [...path, 'nodes', dataBefore.nodes.findIndex((n) => n.uid === node.uid)],
					li: node,
					ld: dataBefore.nodes.find((n) => n.uid === node.uid),
				})),
				{},
				shareDbCallback(
						`Updated ${nodes.length} nodes for (${userFieldName}, ${
							isCollaborative ? 'collaborative' : 'personal'
						})`, additionalShareDBCallback
					)
			);
		},
		updateIfMoved: (
			nodes: NCNode[],
			callback: (() => void) | null = null, 
			additionalShareDBCallback: (() => void) | null = null
		): void => {
			if (nodes.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			if (!dataBefore) {
				throw new Error("Can't update nodes without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}
			const oldNodes = dataBefore.nodes;

			// Only update dragged nodes or nodes that have moved more than 100px

			const nodesToUpdate = nodes.filter((node) => {
				const oldNode = oldNodes.find((n) => n.uid === node.uid);
				if (!oldNode) {
					return true;
				}
				const isDragged = globalThis.neuroCreate.graph?.currentDraggedNodeCluster?.some((n) => n.uid === node.uid);
				if (isDragged) {
					return true;
				}
				const diffX = Math.abs(oldNode.x - node.x);
				const diffY = Math.abs(oldNode.y - node.y);
				const moveDistance = Math.hypot(diffX, diffY);

				return moveDistance > 50;
			});
			if (nodesToUpdate.length === 0) {
				callback && callback();
				return;
			}

			doc.submitOp(
				nodesToUpdate.map((node) => ({
					p: [...path, 'nodes', dataBefore.nodes.findIndex((n) => n.uid === node.uid)],
					li: node,
					ld: dataBefore.nodes.find((n) => n.uid === node.uid),
				})),
				{},
				shareDbCallback(
					`Updated ${nodesToUpdate.length} nodes which moved for (${userFieldName}, ${
						isCollaborative ? 'collaborative' : 'personal'
					})`, additionalShareDBCallback
				)
			);
		},
		delete: (nodes: NCNode[], callback: (() => void) | null = null): void => {
			if (nodes.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore, colour] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			if (window.location.hash !== '#ideate-team') {
				nodes = nodes.filter((n) => n.colour === colour);
			}

			console.log('[nodes to delete]', nodes);
			

			if (!dataBefore) {
				throw new Error("Can't delete nodes without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}

			const nodesAndIndices = nodes.map((node) => {
				const index = dataBefore.nodes.findIndex((n) => n.uid === node.uid);
				return { node, index };
			});
			// Sort into reverse index order so that we can delete from the end of the array first
			nodesAndIndices.sort((a, b) => b.index - a.index);
			console.log('[nodesAndIndices]', userFieldName, nodesAndIndices);

			doc.submitOp(
				nodesAndIndices.map((item) => ({
					p: [...path, 'nodes', item.index],
					ld: item.node,
				})),
				{},
				shareDbCallback(
					`Deleted ${nodes.length} nodes for (${userFieldName}, ${
						isCollaborative ? 'collaborative' : 'personal'
					})`
				)
			);
		},
	};
};

export const makeSaveLinksFunctions = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName,
	userId: string,
	isCollaborative: boolean,
	graphMode: GraphMode
) => {
	return {
		create: (links: NCLink[], callback: (() => void) | null = null): void => {
			if (links.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			if (!dataBefore) {
				throw new Error("Can't save links without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}

			doc.submitOp(
				minimizeLinks(links).map((link, i) => ({
					p: [...path, 'links', dataBefore.links.length + i],
					li: link,
				})),
				{},
				shareDbCallback(
					`Created ${links.length} links for (${userFieldName}, ${
						isCollaborative ? 'collaborative' : 'personal'
					})`
				)
			);
		},
		update: (links: NCLink[], callback: (() => void) | null = null): void => {
			if (links.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			if (!dataBefore) {
				throw new Error("Can't update links without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}

			doc.submitOp(
				minimizeLinks(links).map((link) => ({
					p: [...path, 'links', dataBefore.links.findIndex((l) => isSameLink(l, link))],
					li: link,
					ld: dataBefore.links.find((l) => isSameLink(l, link)),
				})),
				{},
				shareDbCallback(
					`Updated ${links.length} links for (${userFieldName}, ${
						isCollaborative ? 'collaborative' : 'personal'
					})`
				)
			);
		},
		delete: (links: NCLink[], callback: (() => void) | null = null): void => {
			if (links.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			if (!dataBefore) {
				throw new Error("Can't delete links without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}

			const linksAndIndices = minimizeLinks(links).map((link) => {
				const index = dataBefore.links.findIndex((l) => isSameLink(l, link));
				return { link, index };
			});
			// Sort into reverse index order so that we can delete from the end of the array first
			linksAndIndices.sort((a, b) => b.index - a.index);

			doc.submitOp(
				linksAndIndices.map((item) => ({
					p: [...path, 'links', item.index],
					ld: item.link,
				})),
				{},
				shareDbCallback(
					`Deleted ${links.length} links for (${userFieldName}, ${
						isCollaborative ? 'collaborative' : 'personal'
					})`
				)
			);
		},
		deleteMissing: (links: NCLinkMinimal[], callback: (() => void) | null = null): void => {
			console.log('[deleteMissing called]');

			if (links.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			console.log('[path]', path);

			if (!dataBefore) {
				throw new Error("Can't delete links without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}

			const linksAndIndices = links
				.map((link) => {
					const index = dataBefore.links.findIndex((l) => isSameLink(l, link));
					return { link, index };
				})
				.filter((l) => l.index >= 0);
			// Sort into reverse index order so that we can delete from the end of the array first
			linksAndIndices.sort((a, b) => b.index - a.index);

			doc.submitOp(
				linksAndIndices.map((item) => ({
					p: [...path, 'links', item.index],
					ld: item.link,
				})),
				{},
				shareDbCallback(
					`Deleted ${links.length} faulty links for (${userFieldName}, ${
						isCollaborative ? 'collaborative' : 'personal'
					})`
				)
			);
		},
	};
};

export const makeSaveClustersFunctions = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName,
	userId: string,
	isCollaborative: boolean,
	graphMode: GraphMode
) => {
	return {
		create: (clusters: NCCluster[], callback: (() => void) | null = null): void => {
			if (clusters.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			if (!dataBefore) {
				throw new Error("Can't save clusters without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}

			const ops = [];
			if (!dataBefore.clusters) {
				ops.push({
					p: [...path, 'clusters'],
					oi: [],
				});
			}
			ops.push(
				...clusters.map((cluster, i) => ({
					p: [...path, 'clusters', (dataBefore.clusters?.length || 0) + i],
					li: cluster,
				}))
			);

			doc.submitOp(
				ops,
				{},
				shareDbCallback(
					`Created ${clusters.length} clusters for (${userFieldName}, ${
						isCollaborative ? 'collaborative' : 'personal'
					})`
				)
			);
		},
		update: (clusters: NCCluster[], callback: (() => void) | null = null): void => {
			if (clusters.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			if (!dataBefore) {
				throw new Error("Can't update clusters without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}

			doc.submitOp(
				clusters.map((cluster) => ({
					p: [
						...path,
						'clusters',
						dataBefore.clusters!.findIndex((c) => isSameCluster(c, cluster)),
					],
					li: cluster,
					ld: dataBefore.clusters!.find((c) => isSameCluster(c, cluster)),
				})),
				{},
				shareDbCallback(
					`Updated ${clusters.length} clusters for (${userFieldName}, ${
						isCollaborative ? 'collaborative' : 'personal'
					})`
				)
			);
		},
		delete: (clusters: NCCluster[], callback: (() => void) | null = null): void => {
			if (clusters.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			if (!dataBefore) {
				throw new Error("Can't delete clusters without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}

			const clustersAndIndices = clusters.map((cluster) => {
				const index = dataBefore.clusters!.findIndex((c) => isSameCluster(c, cluster));
				return { cluster, index };
			});
			// Sort into reverse index order so that we can delete from the end of the array first
			clustersAndIndices.sort((a, b) => b.index - a.index);

			doc.submitOp(
				clustersAndIndices.map((item) => ({
					p: [...path, 'clusters', item.index],
					ld: item.cluster,
				})),
				{},
				shareDbCallback(
					`Deleted ${clusters.length} clusters for (${userFieldName}, ${
						isCollaborative ? 'collaborative' : 'personal'
					})`
				)
			);
		},
	};
};

export const makeSaveGroupsFunctions = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName,
	userId: string,
	isCollaborative: boolean,
	graphMode: GraphMode
) => {
	return {
		create: (groups: NCGroup[], callback: (() => void) | null = null): void => {
			if (groups.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			if (!dataBefore) {
				throw new Error("Can't save groups without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}

			const ops = [];
			if (!dataBefore.groups) {
				ops.push({
					p: [...path, 'groups'],
					oi: [],
				});
			}
			ops.push(
				...minimizeGroups(groups).map((group, i) => ({
					p: [...path, 'groups', (dataBefore.groups?.length || 0) + i],
					li: group,
				}))
			);

			doc.submitOp(
				ops,
				{},
				shareDbCallback(
					`Created ${groups.length} groups for (${userFieldName}, ${
						isCollaborative ? 'collaborative' : 'personal'
					})`
				)
			);
		},
		update: (groups: NCGroup[], callback: (() => void) | null = null): void => {
			if (groups.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			if (!dataBefore) {
				throw new Error("Can't update groups without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}

			const ops = [];
			const groupsBefore = dataBefore.groups || [];
			ops.push(
				...minimizeGroups(groups).map((group) => ({
					p: [...path, 'groups', groupsBefore.findIndex((g) => g.uid === group.uid)],
					li: group,
					ld: groupsBefore.find((g) => g.uid === group.uid),
				}))
			);

			doc.submitOp(
				ops,
				{},
				shareDbCallback(
					`Updated ${groups.length} groups for (${userFieldName}, ${
						isCollaborative ? 'collaborative' : 'personal'
					})`
				)
			);
		},
		delete: (groups: NCGroup[], callback: (() => void) | null = null): void => {
			if (groups.length === 0) {
				callback && callback();
				return;
			}

			const [path, dataBefore] = getGraphPathAndData(
				doc,
				userFieldName,
				userId,
				isCollaborative,
				graphMode
			);

			if (!dataBefore) {
				throw new Error("Can't delete groups without existing graph data");
			}

			if (callback) {
				globalThis.neuroCreate.graph?.onLoadNewGraphData(callback);
			}

			const groupsAndIndices = minimizeGroups(groups).map((group) => {
				const index = dataBefore.groups!.findIndex((g) => g.uid === group.uid);
				return { group, index };
			});
			// Sort into reverse index order so that we can delete from the end of the array first
			groupsAndIndices.sort((a, b) => b.index - a.index);

			doc.submitOp(
				groupsAndIndices.map((item) => ({
					p: [...path, 'groups', item.index],
					ld: item.group,
				})),
				{},
				shareDbCallback(
					`Deleted ${groups.length} groups for (${userFieldName}, ${
						isCollaborative ? 'collaborative' : 'personal'
					})`
				)
			);
		},
	};
};

export const getGraphName = (graphMode: GraphMode): string => {
	if (graphMode === 'inspire') return 'Inspire';
	else if (graphMode === 'ideate') return 'Ideate';
	else return 'Synthesise';
};

/* ------------------------------------------------------------------ */
/* Personal Synthesise (Moodboard) sub-graph helpers                  */
/*                                                                    */
/* These let us read/write the current user's personal moodboard      */
/* sub-graph from any board mode (e.g. while the user is viewing the  */
/* Inspire board). They are used to keep the Synthesise board in sync */
/* when the user hearts/unhearts/deletes Inspire nodes.               */
/* ------------------------------------------------------------------ */

/**
 * Returns the current user's personal moodboard nodes from the ShareDB
 * doc, or an empty array if the slot hasn't been initialised yet.
 */
export const getPersonalMoodboardNodes = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName
): Array<NCNode> => {
	return doc.data?.[userFieldName]?.moodboard?.data?.nodes || [];
};

/**
 * Best-effort lookup for the node on a given graph that corresponds to
 * an Idea Box `LikedItem`. Used by the Idea Box to decide whether the
 * "+" button should be disabled (the item is already on the active
 * board) or active (the item is missing and clicking will add it).
 *
 * The caller passes the node array of whichever graph the "+" would
 * affect — typically the currently active graph (personal Synthesise,
 * team Synthesise, or Inspire). This keeps the matcher agnostic to the
 * underlying ShareDB path.
 *
 * Matching strategy, in order of confidence:
 *  1. By `uid` when the LikedItem carries a `nodeUid` (post-integration
 *     items hearted from an Inspire node).
 *  2. By the URL stored on `node.src` or `node.href` for media / link
 *     items (search-image, image-inspire, video-inspire, search-page,
 *     article).
 *  3. By exact label match on `node.label` for textual items
 *     (node, haiku, proverb, brainstorm, taglines, …).
 *
 * Complex items whose mapping to one or more nodes is ambiguous
 * (cluster, persona, motivations) intentionally return undefined so the
 * "+" button stays enabled — those flows let the user pick which
 * sub-element to add and we don't try to second-guess them.
 */
export const findMatchingNodeForLikedItem = (
	nodes: ReadonlyArray<NCNode>,
	item: { nodeUid?: string; text?: string; url?: string }
): NCNode | undefined => {
	if (item.nodeUid) {
		const byUid = nodes.find((n) => n.uid === item.nodeUid);
		if (byUid) {
			return byUid;
		}
	}
	if (item.url) {
		const byUrl = nodes.find((n) => n.src === item.url || n.href === item.url);
		if (byUrl) {
			return byUrl;
		}
	}
	if (item.text) {
		const byLabel = nodes.find((n) => n.label === item.text);
		if (byLabel) {
			return byLabel;
		}
	}
	return undefined;
};

/**
 * Ensures the personal moodboard data slot exists for the given user.
 * If the slot is missing, submits a ShareDB op that initialises it
 * with an empty graph. Mirrors the lazy-init logic in extractGraphData.
 */
const ensurePersonalMoodboardInitialised = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName,
	callback: () => void
): void => {
	if (doc.data?.[userFieldName]?.moodboard?.data) {
		callback();
		return;
	}
	doc.submitOp(
		[
			{
				p: [userFieldName, 'moodboard'],
				oi: { data: { nodes: [], links: [] } },
			},
		],
		{},
		shareDbCallback(`Lazy-initialised personal moodboard for ${userFieldName}`, callback)
	);
};

/**
 * Adds a node to the user's personal moodboard sub-graph if a node with
 * the same uid is not already present. No-ops when the node already
 * exists, so the call is idempotent.
 */
export const addNodeToPersonalMoodboard = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName,
	node: NCNode
): void => {
	ensurePersonalMoodboardInitialised(doc, userFieldName, () => {
		const existing = doc.data?.[userFieldName]?.moodboard?.data?.nodes || [];
		if (existing.some((n) => n.uid === node.uid)) {
			return;
		}
		doc.submitOp(
			[
				{
					p: [userFieldName, 'moodboard', 'data', 'nodes', existing.length],
					li: node,
				},
			],
			{},
			shareDbCallback(`Added node ${node.uid} to personal moodboard (${userFieldName})`)
		);
	});
};

/**
 * Sets a like flag on the Inspire node with the given `uid`, by submitting
 * a ShareDB op directly to `inspire.data.nodes`. Used to mirror unlike
 * actions from a personal Synthesise board onto the underlying Inspire
 * node so the heart icon stays in sync without re-running Inspire's
 * `toggleLike` (which would also fire the cascade-delete and remove the
 * Synthesise copy). No-ops if the Inspire node is not present.
 */
export const setInspireNodeLike = (
	doc: sharedb.Doc<NCBoard>,
	uid: string,
	currentUser: Pick<BoardUser, 'id' | 'colour'>,
	liked: boolean
): void => {
	const nodes = doc.data?.inspire?.data?.nodes;
	if (!nodes) {
		return;
	}
	const idx = nodes.findIndex((n) => n.uid === uid);
	if (idx < 0) {
		return;
	}
	const node = nodes[idx];
	const userLikeKey = nodeUserLikeKey(currentUser.id);
	const previouslyLiked = Boolean(
		node.likes?.[userLikeKey] || node.likes?.[currentUser.colour]
	);
	if (previouslyLiked === liked) {
		return;
	}
	const likesPath = ['inspire', 'data', 'nodes', idx, 'likes'];
	const ops: sharedb.Op[] = [];
	if (!node.likes) {
		if (liked) ops.push({ p: likesPath, oi: { [userLikeKey]: true } });
	} else {
		if (node.likes[currentUser.colour]) {
			ops.push({ p: [...likesPath, currentUser.colour], od: true });
		}
		if (liked && !node.likes[userLikeKey]) {
			ops.push({ p: [...likesPath, userLikeKey], oi: true });
		} else if (!liked && node.likes[userLikeKey]) {
			ops.push({ p: [...likesPath, userLikeKey], od: true });
		}
	}
	if (ops.length === 0) return;
	doc.submitOp(
		ops,
		{},
		shareDbCallback(`Mirrored ${liked ? 'like' : 'unlike'} for inspire node ${uid}`)
	);
};

/**
 * Removes the node with the given uid from the user's personal moodboard
 * sub-graph (and any links involving it). No-ops if the node is not
 * present, so the call is idempotent.
 */
export const removeNodeFromPersonalMoodboard = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName,
	uid: string
): void => {
	const slot = doc.data?.[userFieldName]?.moodboard?.data;
	if (!slot) {
		return;
	}
	const nodes = slot.nodes || [];
	const nodeIndex = nodes.findIndex((n) => n.uid === uid);
	if (nodeIndex < 0) {
		return;
	}
	const ops: sharedb.Op[] = [];
	// Remove any links involving the node first (reverse-indexed so removals don't shift)
	const links = slot.links || [];
	const linkIndices: number[] = [];
	links.forEach((l, i) => {
		if (l.source.uid === uid || l.target.uid === uid) {
			linkIndices.push(i);
		}
	});
	linkIndices
		.sort((a, b) => b - a)
		.forEach((i) => {
			ops.push({
				p: [userFieldName, 'moodboard', 'data', 'links', i],
				ld: links[i],
			});
		});
	ops.push({
		p: [userFieldName, 'moodboard', 'data', 'nodes', nodeIndex],
		ld: nodes[nodeIndex],
	});
	doc.submitOp(
		ops,
		{},
		shareDbCallback(`Removed node ${uid} from personal moodboard (${userFieldName})`)
	);
};

/**
 * Op-batch listener that cascades Inspire-board node deletions into the
 * current user's personal Moodboard. Set up once per board doc so it
 * fires regardless of which graph mode the user is currently viewing.
 *
 * Optional `onCascade` callback fires after any cascade removals have
 * been submitted, so callers (e.g. the Idea Box) can re-render any UI
 * that depends on the personal moodboard's contents.
 */
export const makeInspireDeleteCascadeListener = (
	doc: sharedb.Doc<NCBoard>,
	userFieldName: UserFieldName,
	onCascade?: () => void
) => {
	return (ops: Array<sharedb.Op>): void => {
		let cascaded = false;
		for (const op of ops) {
			const path = op.p;
			if (
				path?.length === 4 &&
				path[0] === 'inspire' &&
				path[1] === 'data' &&
				path[2] === 'nodes' &&
				'ld' in op &&
				!('li' in op)
			) {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				const deleted: NCNode | undefined = op.ld;
				const deletedUid = deleted?.uid;
				if (deletedUid) {
					const presentBefore = (
						doc.data?.[userFieldName]?.moodboard?.data?.nodes || []
					).some((n) => n.uid === deletedUid);
					removeNodeFromPersonalMoodboard(doc, userFieldName, deletedUid);
					if (presentBefore) {
						cascaded = true;
					}
				}
			}
		}
		if (cascaded && onCascade) {
			onCascade();
		}
	};
};
